import type { PaymentRoutesTranslations } from "@/shared/lang/interfaces/paymentRoutes";
import { formatRail } from "@/shared/utils/railDisplay";

import { providerCoverage } from "../constants/paymentRoutes.constants";
import { PaymentDirection } from "../types/paymentFees.types";
import type { CorridorRouteCell, CorridorRouteStatus } from "../types/paymentRoutes.types";

/**
 * Display helpers for the provider-routes surface. Pure functions, so every translated label is injected by
 * the caller.
 *
 * Rail names go through `formatRail` (they are proper nouns and are never translated), and the provider is
 * only ever described by the corridors it covers — its `provider_code` never reaches the screen (rule 12).
 */

/** `MEX · SPEI · MXN · Salidas` — the corridor identity used in headings, chips and warnings. */
export function corridorCellLabel(
  cell: Pick<CorridorRouteCell, "country" | "rail" | "currency" | "direction">,
  directionLabels: { payin: string; payout: string },
): string {
  const direction =
    cell.direction === PaymentDirection.PAYIN ? directionLabels.payin : directionLabels.payout;
  return `${cell.country} · ${formatRail(cell.rail)} · ${cell.currency} · ${direction}`;
}

/** `MEX · SPEI` per declared corridor — the provider-neutral way to show what a provider can serve. */
export function providerCoverageChips(providerCode: string): string[] {
  return providerCoverage(providerCode).map(
    (corridor) => `${corridor.country} · ${formatRail(corridor.rail)}`,
  );
}

/**
 * Derived status → `ColocaStatusPill` variant.
 *
 * `BLOCKED` and `UNSUPPORTED` are warnings rather than errors: the corridor is misconfigured, but unlike
 * `MISSING` the operator can see a row and act on it. `MISSING` is the destructive one — that corridor
 * refuses every operation.
 */
export const ROUTE_STATUS_PILL_VARIANT: Record<
  CorridorRouteStatus,
  "success" | "warning" | "danger" | "neutral" | "info"
> = {
  ROUTED: "success",
  MISSING: "danger",
  BLOCKED: "warning",
  UNSUPPORTED: "warning",
};

/**
 * I18n key of each status label and of its one-line consequence, so the table, the cards and the drawer can
 * never word one differently. Typed against the namespace, which is what lets `t(...)` stay checked at the
 * call site instead of taking a widened `string`.
 */
export const ROUTE_STATUS_LABEL_KEY: Record<CorridorRouteStatus, keyof PaymentRoutesTranslations> = {
  ROUTED: "status_routed",
  MISSING: "status_missing",
  BLOCKED: "status_blocked",
  UNSUPPORTED: "status_unsupported",
};

/** What breaks in each state, in the operator's terms. */
export const ROUTE_STATUS_CONSEQUENCE_KEY: Record<CorridorRouteStatus, keyof PaymentRoutesTranslations> = {
  ROUTED: "consequence_routed",
  MISSING: "consequence_missing",
  BLOCKED: "consequence_blocked",
  UNSUPPORTED: "consequence_unsupported",
};
